import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { usePage } from "@inertiajs/react";
import { useState } from "react";
import DomainTestUserConfigured from "@/components/domains/DomainTestUserConfigured";
import DomainTestUserForm from "@/components/domains/DomainTestUserForm";
import type { DomainTestUser } from "@/types";

type Props = {
  domainId: string;
  testUser: DomainTestUser;
};

export default function DomainTestUserSection({ domainId, testUser }: Props) {
  const { errors } = usePage().props;
  const hasFormErrors = Boolean(errors.email || errors.password || errors.instructions || errors.auth_method);
  const [editing, setEditing] = useState(!testUser.configured || hasFormErrors);
  const showForm = testUser.can_configure && (editing || !testUser.configured || hasFormErrors);

  if (!testUser.can_configure) {
    return null;
  }

  return (
    <div className="mt-6">
      {showForm ? (
        <>
          <p className="text-sm text-gray-500">{promptCopy(testUser)}</p>
          {testUser.mapping_guidance ? <p className="mt-2 text-sm text-gray-500">{testUser.mapping_guidance}</p> : null}

          <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-gray-700">
              We'll sign in as this user, map journeys, and run Audits on your website. We recommend using a throwaway
              account or staging environment, as mapping and later Audits can change data.
            </p>
          </div>

          <DomainTestUserForm
            domainId={domainId}
            testUser={testUser}
            onCancel={testUser.configured ? () => setEditing(false) : undefined}
            onSaved={() => setEditing(false)}
          />
        </>
      ) : (
        <DomainTestUserConfigured domainId={domainId} testUser={testUser} onEdit={() => setEditing(true)} />
      )}
    </div>
  );
}

function promptCopy(testUser: DomainTestUser) {
  if (testUser.prompt === "insist") {
    return "Add a throwaway test user so we can audit your signed-in product.";
  }

  return "Have a signed-in experience? Add a throwaway test user so we can audit it.";
}
